import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
  Visibility,
  Edit,
  Search,
  Add,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setLoading,
  setError,
  setDocumentTypes,
  setSelectedDocumentType,
  setPage,
  setPageSize,
  setSearch,
  clearSelectedDocumentType,
  addDocumentType,
  updateDocumentType as updateDocumentTypeAction,
  deleteDocumentType as deleteDocumentTypeAction,
} from '../../store/slices/documentTypesSlice';
import {
  GET_DOCUMENT_TYPES_LIST,
  GET_DOCUMENT_TYPE,
} from '../../graphql/queries/documentTypes.queries';
import {
  CREATE_DOCUMENT_TYPE,
  UPDATE_DOCUMENT_TYPE,
  DELETE_DOCUMENT_TYPE,
} from '../../graphql/mutations/documentTypes.mutations';
import {
  FindDocumentTypeListInput,
  FindDocumentTypeInput,
  CreateDocumentTypeInput,
  UpdateDocumentTypeInput,
  DeleteDocumentTypeInput,
  CreateDocumentTypeResponse,
  UpdateDocumentTypeResponse,
  DeleteDocumentTypeResponse,
  DocumentTypesListResponse,
  DocumentTypeResponse,
  DocumentType,
} from '../../types/documentTypes.types';
import ViewDocumentTypeModal from '../../components/DocumentTypes/ViewDocumentTypeModal';
import CreateEditDocumentTypeModal, {
  DocumentTypeFormData,
} from '../../components/DocumentTypes/CreateEditDocumentTypeModal';
import MainLayout from '../../components/Layout/MainLayout';
import './DocumentTypes.css';

const DocumentTypes: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { documentTypes, selectedDocumentType, loading, error, pagination, search } = useAppSelector(
    (state) => state.documentTypes
  );

  const [searchInput, setSearchInput] = useState(search);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [createEditModalOpen, setCreateEditModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [fetchDocumentTypes, { loading: fetchingList }] = useLazyQuery<
    { chatbotDocumentTypes: DocumentTypesListResponse },
    { params: FindDocumentTypeListInput }
  >(GET_DOCUMENT_TYPES_LIST, {
    fetchPolicy: 'network-only',
  });

  const [fetchDocumentType, { loading: fetchingOne }] = useLazyQuery<
    { chatbotDocumentType: DocumentTypeResponse },
    { params: FindDocumentTypeInput }
  >(GET_DOCUMENT_TYPE, {
    fetchPolicy: 'network-only',
  });

  const [createDocumentTypeMutation] = useMutation<
    { createChatbotDocumentType: CreateDocumentTypeResponse },
    { params: CreateDocumentTypeInput }
  >(CREATE_DOCUMENT_TYPE);

  const [updateDocumentTypeMutation] = useMutation<
    { updateChatbotDocumentType: UpdateDocumentTypeResponse },
    { params: UpdateDocumentTypeInput }
  >(UPDATE_DOCUMENT_TYPE);

  const [deleteDocumentTypeMutation] = useMutation<
    { deleteChatbotDocumentType: DeleteDocumentTypeResponse },
    { params: DeleteDocumentTypeInput }
  >(DELETE_DOCUMENT_TYPE);

  useEffect(() => {
    loadDocumentTypes();
  }, [pagination.page, pagination.pageSize, search]);

  const loadDocumentTypes = async () => {
    try {
      dispatch(setLoading(true));

      const { data } = await fetchDocumentTypes({
        variables: {
          params: {
            search: search || null,
            page: pagination.page,
            pageSize: pagination.pageSize,
          },
        },
      });

      if (data?.chatbotDocumentTypes?.success && data.chatbotDocumentTypes.data) {
        dispatch(setDocumentTypes(data.chatbotDocumentTypes.data));
      } else {
        const errorMessage =
          data?.chatbotDocumentTypes?.message || 'Error al cargar tipos de documentos';
        dispatch(setError(errorMessage));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      console.error('Error loading document types:', err);
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSearch = () => {
    dispatch(setSearch(searchInput));
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePaginationChange = (model: GridPaginationModel) => {
    dispatch(setPage(model.page + 1));
    dispatch(setPageSize(model.pageSize));
  };

  const handleView = async (id: string) => {
    try {
      dispatch(setLoading(true));

      const { data } = await fetchDocumentType({
        variables: {
          params: {
            ids: [id],
          },
        },
      });

      if (data?.chatbotDocumentType?.success && data.chatbotDocumentType.data) {
        const documentType = data.chatbotDocumentType.data[0];
        dispatch(setSelectedDocumentType(documentType));
        setViewModalOpen(true);
      } else {
        const errorMessage =
          data?.chatbotDocumentType?.message || 'Error al cargar tipo de documento';
        dispatch(setError(errorMessage));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      console.error('Error loading document type:', err);
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleEdit = async (id: string) => {
    try {
      dispatch(setLoading(true));

      const { data } = await fetchDocumentType({
        variables: {
          params: {
            ids: [id],
          },
        },
      });

      if (data?.chatbotDocumentType?.success && data.chatbotDocumentType.data) {
        const documentType = data.chatbotDocumentType.data[0];
        dispatch(setSelectedDocumentType(documentType));
        setModalMode('edit');
        setCreateEditModalOpen(true);
      } else {
        const errorMessage =
          data?.chatbotDocumentType?.message || 'Error al cargar tipo de documento';
        dispatch(setError(errorMessage));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      console.error('Error loading document type:', err);
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleCreate = () => {
    dispatch(clearSelectedDocumentType());
    setModalMode('create');
    setSaveError(null);
    setCreateEditModalOpen(true);
  };

  const handleSave = async (data: DocumentTypeFormData) => {
    try {
      setSaving(true);
      setSaveError(null);

      if (modalMode === 'create') {
        // Crear nuevo tipo de documento
        const { data: responseData } = await createDocumentTypeMutation({
          variables: {
            params: {
              code: data.code,
              name: data.name,
              description: data.description,
              chunkSize: data.chunkSize,
              chunkOverlap: data.chunkOverlap,
            },
          },
        });

        if (responseData?.createChatbotDocumentType?.success) {
          // Creación exitosa
          if (responseData.createChatbotDocumentType.data) {
            // Agregar a Redux sin recargar
            dispatch(addDocumentType(responseData.createChatbotDocumentType.data));
          }
          // Cerrar modal y limpiar
          setCreateEditModalOpen(false);
          dispatch(clearSelectedDocumentType());
          setSaveError(null);
        } else {
          // Error en la creación
          const errorMessage =
            responseData?.createChatbotDocumentType?.message || 'Error al crear tipo de documento';
          const errors = responseData?.createChatbotDocumentType?.errors || [];
          const fullErrorMessage =
            errors.length > 0 ? `${errorMessage}: ${errors.join(', ')}` : errorMessage;

          setSaveError(fullErrorMessage);
          // NO cerrar el modal
        }
      } else {
        // Actualizar tipo de documento existente
        if (!data.id) {
          setSaveError('ID de documento no disponible');
          return;
        }

        const { data: responseData } = await updateDocumentTypeMutation({
          variables: {
            params: {
              id: data.id,
              code: data.code,
              name: data.name,
              description: data.description,
              chunkSize: data.chunkSize,
              chunkOverlap: data.chunkOverlap,
            },
          },
        });

        if (responseData?.updateChatbotDocumentType?.success) {
          // Actualización exitosa
          if (responseData.updateChatbotDocumentType.data) {
            // Actualizar en Redux sin recargar
            dispatch(updateDocumentTypeAction(responseData.updateChatbotDocumentType.data));
          }
          // Cerrar modal y limpiar
          setCreateEditModalOpen(false);
          dispatch(clearSelectedDocumentType());
          setSaveError(null);
        } else {
          // Error en la actualización
          const errorMessage =
            responseData?.updateChatbotDocumentType?.message ||
            'Error al actualizar tipo de documento';
          const errors = responseData?.updateChatbotDocumentType?.errors || [];
          const fullErrorMessage =
            errors.length > 0 ? `${errorMessage}: ${errors.join(', ')}` : errorMessage;

          setSaveError(fullErrorMessage);
          // NO cerrar el modal
        }
      }
    } catch (err: any) {
      console.error('Error saving document type:', err);
      setSaveError(err.message || 'Error de conexión con el servidor');
      // NO cerrar el modal
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedDocumentType) {
      return;
    }

    // Confirmación antes de eliminar
    if (
      !window.confirm(
        `¿Está seguro que desea eliminar el tipo de documento "${selectedDocumentType.name}"?`
      )
    ) {
      return;
    }

    try {
      setDeleting(true);
      setSaveError(null);

      const { data: responseData } = await deleteDocumentTypeMutation({
        variables: {
          params: {
            ids: [selectedDocumentType.id],
          },
        },
      });

      if (responseData?.deleteChatbotDocumentType?.success) {
        // Eliminación exitosa
        dispatch(deleteDocumentTypeAction(selectedDocumentType.id));
        // Cerrar modal y limpiar
        setCreateEditModalOpen(false);
        dispatch(clearSelectedDocumentType());
        setSaveError(null);
      } else {
        // Error en la eliminación
        const errorMessage =
          responseData?.deleteChatbotDocumentType?.message || 'Error al eliminar tipo de documento';
        const errors = responseData?.deleteChatbotDocumentType?.errors || [];
        const fullErrorMessage =
          errors.length > 0 ? `${errorMessage}: ${errors.join(', ')}` : errorMessage;

        setSaveError(fullErrorMessage);
        // NO cerrar el modal
      }
    } catch (err: any) {
      console.error('Error deleting document type:', err);
      setSaveError(err.message || 'Error de conexión con el servidor');
      // NO cerrar el modal
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    dispatch(clearSelectedDocumentType());
  };

  const handleCloseCreateEditModal = () => {
    setCreateEditModalOpen(false);
    dispatch(clearSelectedDocumentType());
    setSaveError(null);
    setSaving(false);
    setDeleting(false);
  };

  const columns: GridColDef<DocumentType>[] = [
    {
      field: 'code',
      headerName: 'Código',
      width: 150,
    },
    {
      field: 'name',
      headerName: 'Nombre',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'description',
      headerName: 'Descripción',
      flex: 2,
      minWidth: 250,
    },
    {
      field: 'chunkSize',
      headerName: 'Chunk Size',
      width: 120,
    },
    {
      field: 'chunkOverlap',
      headerName: 'Chunk Overlap',
      width: 130,
    },
    {
      field: 'createdAt',
      headerName: 'Fecha Creación',
      width: 130,
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleView(params.row.id)}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              color="secondary"
              onClick={() => handleEdit(params.row.id)}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="document-types-container">
        <Paper elevation={2} className="document-types-paper">
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              color="inherit"
              onClick={() => navigate('/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
              Inicio
            </Link>
            <Typography color="text.primary">Tipos de Documentos</Typography>
          </Breadcrumbs>

          <Box className="document-types-header">
            <Typography variant="h4" component="h1">
              Tipos de Documentos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Administración de tipos de documentos del sistema
            </Typography>
          </Box>

          <Box className="document-types-toolbar">
            <Box className="search-box">
              <TextField
                size="small"
                placeholder="Buscar tipo de documento..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                sx={{ minWidth: 300 }}
              />
              <Button
                variant="contained"
                startIcon={<Search />}
                onClick={handleSearch}
                sx={{ ml: 1 }}
              >
                Buscar
              </Button>
            </Box>

            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreate}
              sx={{ backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' } }}
            >
              Nuevo Tipo de Documento
            </Button>
          </Box>

          {error && (
            <Box className="error-box">
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          <Box className="data-grid-container">
            <DataGrid
              rows={documentTypes}
              columns={columns}
              loading={loading || fetchingList}
              pageSizeOptions={[5, 10, 25, 50]}
              paginationModel={{
                page: pagination.page - 1,
                pageSize: pagination.pageSize,
              }}
              onPaginationModelChange={handlePaginationChange}
              paginationMode="server"
              rowCount={pagination.totalRecords}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            />
          </Box>
        </Paper>

        <ViewDocumentTypeModal open={viewModalOpen} onClose={handleCloseViewModal} />
        <CreateEditDocumentTypeModal
          open={createEditModalOpen}
          onClose={handleCloseCreateEditModal}
          onSave={handleSave}
          onDelete={handleDelete}
          mode={modalMode}
          saving={saving}
          deleting={deleting}
          saveError={saveError}
        />
      </div>
    </MainLayout>
  );
};

export default DocumentTypes;
